import { useEffect, useRef } from 'react';
import { Workbook, WorkbookInstance } from '@fortune-sheet/react';
// Registers the Excel functions the formula engine lacks before any workbook's parser exists.
import './formulaFunctions';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useLazySheets } from './useLazySheets';
import { useResolveSheets } from './useResolveSheets';
import { tooLargeToPreviewMessage, LARGE_FILE_NOTICE } from './sheetSizeGuard';
import { FortuneSheet } from './types';
import { DEFAULT_COL_WIDTH, MIN_ROW_HEIGHT } from './fileTypes';
import { ensureSheetFontList } from './fortuneSheetSetup';
import styles from './SheetView.module.scss';
import '@fortune-sheet/react/dist/index.css';
import './fortuneSheetOverrides.css';

// Extend the toolbar font list once, before any <Workbook> mounts.
ensureSheetFontList();

type SheetViewSource =
    | { fileUrl: string; sheets?: never }
    | {
          /** In-memory sheets, when already resolved by the caller. Skips the fetch. */
          sheets: FortuneSheet[];
          fileUrl?: never;
      };

export type SheetViewProps = SheetViewSource & {
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Original file name — used only as the workbook's remount key on the fetch path. */
    fileName?: string;
};

/**
 * Read-only inline Fortune-Sheet grid — the universal spreadsheet PREVIEW
 * (DocumentViewer's spreadsheet branch, and any caller with in-memory sheets).
 * No drawer chrome, no download/save — those stay on the editable
 * SheetEditorDrawer. Relies entirely on Fortune-Sheet's own canvas
 * virtualization for large sheets — no row/col preview cap is reimposed here.
 */
const SheetView: React.FC<SheetViewProps> = ({ fileUrl, sheets: inMemorySheets, mimeType, fileName }) => {
    // Resolve sheets (in-memory or fetch+parse) — shared with the editable drawer.
    // No emptyFallback: a zero-sheet parse stays empty so the "no data" state shows.
    const { sheets, loading, loadingStage, error, tooLarge, largeNotice } = useResolveSheets({
        inMemorySheets,
        fileUrl,
        mimeType,
        fileName,
        context: 'preview',
    });

    // Large multi-tab workbooks preview with only the active sheet hydrated;
    // the read-only viewer never persists, so no getFullSheets merge is needed.
    const workbookRef = useRef<WorkbookInstance>(null);
    const { workbookSheets, hydratePending } = useLazySheets({ sheets, workbookRef });

    // Fill stubs in right after the Workbook mounts (post-first-paint).
    useEffect(() => {
        if (!loading && !error && workbookSheets?.length) hydratePending();
    }, [loading, error, workbookSheets, hydratePending]);

    const loadingStatusText =
        loadingStage === 'downloading' ? 'Downloading file…' : loadingStage === 'parsing' ? 'Preparing spreadsheet…' : null;

    if (loading) {
        return (
            <div className={styles.statusBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                {loadingStatusText && <p className={styles.statusLine}>{loadingStatusText}</p>}
                {largeNotice && <p className={styles.statusLine}>{LARGE_FILE_NOTICE}</p>}
            </div>
        );
    }

    if (tooLarge) {
        return (
            <div className={styles.statusBox}>
                <p>{tooLargeToPreviewMessage(tooLarge.cells, tooLarge.basis)}</p>
            </div>
        );
    }

    if (error || !sheets?.length) {
        return (
            <div className={styles.statusBox}>
                <p>{error ? 'Unable to preview this file' : 'This file has no data to preview'}</p>
            </div>
        );
    }

    return (
        <div className={styles.sheetViewWrapper}>
            <Workbook
                key={fileUrl ?? fileName}
                ref={workbookRef}
                data={workbookSheets!}
                lang="en"
                showToolbar
                showFormulaBar
                showSheetTabs
                allowEdit={false}
                defaultRowHeight={MIN_ROW_HEIGHT}
                defaultColWidth={DEFAULT_COL_WIDTH}
            />
        </div>
    );
};

export default SheetView;
